import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { UserService } from '../../../../core/services/user.service';
import {
  CountryService,
  Country,
} from '../../../../core/services/country.service';
import { ModalService } from '../../../../core/services/modal.service';
import { CreateSubAccountDto } from '../../../../core/interfaces/user.interface';
import { noWhitespaceValidator } from '../../../../shared/validators';

@Component({
  selector: 'app-add-sub-account-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './add-sub-account-modal.component.html',
  styleUrls: ['./add-sub-account-modal.component.scss'],
})
export class AddSubAccountModalComponent implements OnInit {
  @Output() subAccountCreated = new EventEmitter<any>();

  form: FormGroup;
  loading: boolean = false;
  error: string | null = null;
  success: boolean = false;
  countries: Country[] = [];
  loadingCountries: boolean = false;



  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private countryService: CountryService,
    private modalService: ModalService
  ) {
    this.form = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2), noWhitespaceValidator]],
      lastName: ['', [Validators.required, Validators.minLength(2), noWhitespaceValidator]],
      sex: ['', Validators.required],
      birthday: [
        '',
        [Validators.required, this.futureDateValidator.bind(this)],
      ],
      nationality: ['SN', Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadCountries();

    // Écouter les changements de pays pour mettre à jour les régions/départements
    this.form.get('address.country')?.valueChanges.subscribe((country) => {
      if (country.code === 'SN') {
        this.form.patchValue({
          address: {
            region: 'Dakar',
            departement: 'Dakar',
            commune: 'Dakar',
          },
        });
      }
    });
  }

  // Validateur personnalisé pour empêcher les dates futures
  futureDateValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // La validation required s'en occupe
      }

      const selectedDate = new Date(control.value);
      const today = new Date();

      // Réinitialiser les heures pour comparer seulement les dates
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      console.log('Date sélectionnée:', selectedDate);
      console.log("Date aujourd'hui:", today);
      console.log('Est dans le futur?', selectedDate > today);

      if (selectedDate > today) {
        return { futureDate: true };
      }

      return null;
    };
  }

  loadCountries(): void {
    this.loadingCountries = true;
    this.countryService.getCountriesSorted('fr').subscribe({
      next: (countries) => {
        this.countries = countries;
        this.loadingCountries = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des pays:', error);
        this.loadingCountries = false;
      },
    });
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.loading = true;
      this.error = null;

      const formData = this.form.value;

      // Formater la date de naissance au format DD/MM/YYYY
      const birthday = this.formatDateForAPI(formData.birthday);

      // Trouver le pays sélectionné pour obtenir le nom complet
      const selectedCountry = this.countries.find(
        (country) => country.code === formData.nationality
      );

      const subAccountData: CreateSubAccountDto = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        sex: formData.sex,
        birthday: birthday,
        address: formData.address,
        nationality: {
          code: formData.nationality,
          label: selectedCountry?.name_fr || formData.nationality,
        },
      };

      this.userService.createSubAccount(subAccountData).subscribe({
        next: (response) => {
          this.loading = false;
          this.success = true;

          // Émettre l'événement pour informer le composant parent
          this.subAccountCreated.emit(response);

          // Émettre un événement via le service modal
          this.modalService.emitEvent({
            type: 'subAccountCreated',
            data: response,
          });

          // Fermer le modal après un délai
          setTimeout(() => {
            this.closeModal();
          }, 1500);
        },
        error: (err) => {
          this.loading = false;
          this.error =
            'Erreur lors de la création du sous-compte. Veuillez réessayer.';
          console.error('Erreur création sous-compte:', err);
        },
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  formatDateForAPI(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  closeModal(): void {
    // Utiliser le service modal pour fermer le modal
    this.modalService.closeAll();
  }

  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return 'Ce champ est requis';
      }
      if (control.errors['minlength']) {
        return `Minimum ${control.errors['minlength'].requiredLength} caractères`;
      }
      if (control.errors['whitespace']) {
        return 'Ce champ ne peut pas contenir uniquement des espaces';
      }
      if (control.errors['futureDate']) {
        return 'La date de naissance ne peut pas être dans le futur';
      }
    }
    return '';
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && control?.touched);
  }

  isFieldValid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.valid && control?.touched);
  }

  getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onBirthdayChange(): void {
    const birthdayControl = this.form.get('birthday');
    if (birthdayControl) {
      birthdayControl.markAsTouched();
      birthdayControl.updateValueAndValidity();
    }
  }
}
